import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AuthActions, AuthScreen, AuthSplash } from './AuthShell';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'admin' | 'top_owner' | 'user';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading splash while checking authentication
  if (isLoading) {
    return <AuthSplash />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requiredRole && user) {
    const roleHierarchy = {
      'user': 1,
      'top_owner': 2,
      'admin': 3,
      'super_admin': 4,
    };

    const userRoleLevel = roleHierarchy[user.role];
    const requiredRoleLevel = roleHierarchy[requiredRole];

    if (userRoleLevel < requiredRoleLevel) {
      return (
        <AuthScreen>
          <div className="flex flex-1 flex-col items-center justify-center py-10 text-center sm:py-6">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
              <Lock className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>
            <h1 className="mt-4 text-[15px] font-normal tracking-[-0.01em] text-ink">Access Denied</h1>
            <p className="mt-1 text-[13px] text-neutral-500">Required role: {requiredRole}</p>
          </div>
          <AuthActions>
            <a
              href="/dashboard"
              className="inline-flex h-10 w-full items-center justify-center rounded-full bg-white text-[13px] font-medium text-ink ring-1 ring-inset ring-neutral-200 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 sm:h-9"
            >
              Go to dashboard
            </a>
          </AuthActions>
        </AuthScreen>
      );
    }
  }

  return <>{children}</>;
};